<template>
    <div class="inventory-manager">
        <!-- 인벤토리 목록이 없을 때 -->
        <div v-if="!loading.initialize && inventoryList.length === 0" class="empty-state">
            <div class="inventory-empty-content">
                <i class="fas fa-inbox"></i>
                <h3>생성된 인벤토리가 없습니다</h3>
                <p>워크플로우에서 체크포인트 검증을 통해 인벤토리를 생성할 수 있습니다.</p>
            </div>
        </div>

        <!-- 인벤토리 목록 & 상세 -->
        <div v-else class="manager-body">
            <div class="inventory-grid">
                <!-- 왼쪽: 인벤토리 목록 -->
                <div class="inventory-list-section">
                    <InventoryListTable
                        :inventories="inventoryList"
                        :selected-idx="selectedInventoryIdx"
                        :loading="loading.list || loading.initialize"
                        :disabled="loading.initialize"
                        @select="handleInventorySelect"
                        @delete-selected="handleInventoryDeleteSelected"
                    />
                </div>

                <!-- 오른쪽: 인벤토리 상세 -->
                <div class="inventory-detail-section">
                    <InventoryDetailView
                        :detail="inventoryDetail"
                        :loading="loading.detail"
                        :quality-loading="loading.quality"
                        :uncertainty-loading="loading.uncertainty"
                        :quality-result="qualityResult"
                        :uncertainty-result="uncertaintyResult"
                        @refresh="refreshInventoryDetail"
                        @delete="handleInventoryDelete"
                        @quality-check="handleQualityCheck"
                        @uncertainty-check="handleUncertaintyCheck"
                    />
                </div>
            </div>
        </div>

        <!-- 품질 점검 모달 -->
        <InventoryQualityModal
            :visible.sync="showQualityModal"
            :loading="loading.quality"
            :result="qualityResult"
            @close="handleQualityClose"
        />

        <!-- 불확도 분석 모달 -->
        <b-modal
            v-model="showUncertaintyModal"
            title="불확도 분석"
            size="xl"
            centered
            hide-footer
            @hidden="handleUncertaintyClose"
        >
            <InventoryUncertaintyPanel :loading="loading.uncertainty" :uncertainty="uncertaintyResult" />
        </b-modal>
    </div>
</template>

<script>
import {
    fetchInventoryDetail,
    deleteInventory,
    fetchInventoriesByStep,
    fetchQualityReport,
    fetchUncertainty,
} from "../../services/inventoryService.js";

import InventoryListTable from "./components/InventoryListTable.vue";
import InventoryDetailView from "./components/InventoryDetailView.vue";
import InventoryQualityModal from "./modals/InventoryQualityModal.vue";
import InventoryUncertaintyPanel from "./components/InventoryUncertaintyPanel.vue";

export default {
    components: {
        InventoryListTable,
        InventoryDetailView,
        InventoryQualityModal,
        InventoryUncertaintyPanel,
    },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
        loginUser: {
            type: Object,
            default: () => ({}),
        },
        /** 계산 워크플로우에서 스텝 선택 시 해당 스텝의 인벤토리만 조회 (stepIdx로 API 필터) */
        stepIdx: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: {
                initialize: false,
                list: false,
                detail: false,
                quality: false,
                uncertainty: false,
            },
            inventoryList: [],
            selectedInventoryIdx: null,
            inventoryDetail: null,
            qualityResult: null,
            showQualityModal: false,
            uncertaintyResult: null,
            showUncertaintyModal: false,
            lastUncertaintyIdx: null,
            lastQualityIdx: null,
        };
    },
    mounted() {
        if (this.isActive) {
            this.refreshContext();
        }
    },
    watch: {
        isActive: {
            immediate: true,
            handler(active) {
                if (active) {
                    this.refreshContext();
                }
            },
        },
        stepIdx: {
            handler(val) {
                if (val != null) {
                    this.loadInventoryList();
                }
            },
            immediate: false,
        },
    },
    methods: {
        async refreshContext() {
            await this.initialize();
        },
        async initialize() {
            if (this.loading.initialize) {
                return;
            }
            this.loading.initialize = true;
            try {
                await this.loadInventoryList();
            } catch (error) {
                this.notifyError("초기화 실패", error);
            } finally {
                this.loading.initialize = false;
            }
        },
        resetInventoryState() {
            this.inventoryList = [];
            this.selectedInventoryIdx = null;
            this.inventoryDetail = null;
            this.qualityResult = null;
            this.showQualityModal = false;
        },
        async loadInventoryList() {
            this.loading.list = true;
            try {
                if (this.stepIdx != null) {
                    const response = await fetchInventoriesByStep(this.stepIdx);
                    this.inventoryList = response?.items || [];
                }
            } catch (error) {
                this.notifyError("인벤토리 목록 조회 실패", error);
                this.inventoryList = [];
            } finally {
                this.loading.list = false;
            }
        },
        async handleInventorySelect(inventoryIdx) {
            if (inventoryIdx === this.selectedInventoryIdx) return;

            this.selectedInventoryIdx = inventoryIdx;
            this.loading.detail = true;
            this.qualityResult = null;
            this.uncertaintyResult = null;
            this.lastQualityIdx = null;
            this.lastUncertaintyIdx = null;

            try {
                const response = await fetchInventoryDetail(inventoryIdx);
                this.inventoryDetail = response?.data || response;
                if (this.inventoryDetail) {
                    this.loadQualityForDetail(inventoryIdx);
                    this.loadUncertaintyForDetail(inventoryIdx);
                }
            } catch (error) {
                this.notifyError("인벤토리 상세 조회 실패", error);
                this.inventoryDetail = null;
            } finally {
                this.loading.detail = false;
            }
        },
        async refreshInventoryDetail() {
            if (!this.selectedInventoryIdx) return;
            await this.handleInventorySelect(this.selectedInventoryIdx);
            this.$bvToast.toast("인벤토리가 새로고침되었습니다.", {
                title: "새로고침",
                variant: "success",
                autoHideDelay: 2000,
            });
        },
        async handleInventoryDelete() {
            if (!this.selectedInventoryIdx) return;

            // 삭제 확인
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `인벤토리 #${this.selectedInventoryIdx}를 삭제하시겠습니까?`,
                {
                    title: "인벤토리 삭제",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "삭제",
                    cancelTitle: "취소",
                    centered: true,
                },
            );

            if (!confirmed) return;

            try {
                // 현재 선택된 인벤토리의 인덱스 찾기
                const currentIndex = this.inventoryList.findIndex(
                    (inv) => inv.inventoryIdx === this.selectedInventoryIdx,
                );

                // 삭제 실행
                const result = await deleteInventory(this.selectedInventoryIdx);

                // API 응답의 message 사용
                this.$bvToast.toast(result.message || "인벤토리가 삭제되었습니다.", {
                    title: "삭제 완료",
                    variant: "success",
                    autoHideDelay: 2000,
                });

                // 인벤토리 목록 새로고침
                await this.loadInventoryList();

                // 다음 인벤토리 선택
                if (this.inventoryList.length > 0) {
                    // 현재 인덱스의 다음 항목 선택 (없으면 이전 항목)
                    let nextIndex = currentIndex;
                    if (nextIndex >= this.inventoryList.length) {
                        nextIndex = this.inventoryList.length - 1;
                    }
                    const nextInventory = this.inventoryList[nextIndex];
                    if (nextInventory) {
                        await this.handleInventorySelect(nextInventory.inventoryIdx);
                    }
                } else {
                    // 인벤토리가 더 이상 없음
                    this.selectedInventoryIdx = null;
                    this.inventoryDetail = null;
                }
            } catch (error) {
                this.notifyError("인벤토리 삭제 실패", error);
            }
        },
        async handleInventoryDeleteSelected(inventoryIdxs) {
            if (!inventoryIdxs || inventoryIdxs.length === 0) return;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `선택한 ${inventoryIdxs.length}개의 인벤토리를 삭제하시겠습니까?`,
                {
                    title: "인벤토리 삭제",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    okTitle: "삭제",
                    cancelTitle: "취소",
                    centered: true,
                },
            );

            if (!confirmed) return;

            try {
                for (const idx of inventoryIdxs) {
                    await deleteInventory(idx);
                }
                this.$bvToast.toast(`${inventoryIdxs.length}개의 인벤토리가 삭제되었습니다.`, {
                    title: "삭제 완료",
                    variant: "success",
                    autoHideDelay: 2000,
                });
                await this.loadInventoryList();
                const stillExists = this.inventoryList.some((inv) => inv.inventoryIdx === this.selectedInventoryIdx);
                if (this.inventoryList.length > 0 && !stillExists) {
                    await this.handleInventorySelect(this.inventoryList[0].inventoryIdx);
                } else if (this.inventoryList.length === 0) {
                    this.selectedInventoryIdx = null;
                    this.inventoryDetail = null;
                }
            } catch (error) {
                this.notifyError("인벤토리 삭제 실패", error);
            }
        },
        handleQualityClose() {
            this.showQualityModal = false;
        },
        async handleQualityCheck(inventoryIdx) {
            if (!inventoryIdx || this.loading.quality) return;
            if (this.lastQualityIdx === inventoryIdx && this.qualityResult) {
                this.showQualityModal = true;
                return;
            }
            this.loading.quality = true;
            try {
                this.qualityResult = await fetchQualityReport(inventoryIdx);
                this.lastQualityIdx = inventoryIdx;
                this.showQualityModal = true;
            } catch (error) {
                this.notifyError("품질 점검에 실패했습니다.", error);
            } finally {
                this.loading.quality = false;
            }
        },
        async handleUncertaintyCheck(inventoryIdx) {
            if (!inventoryIdx || this.loading.uncertainty) return;
            if (this.lastUncertaintyIdx === inventoryIdx && this.uncertaintyResult) {
                this.showUncertaintyModal = true;
                return;
            }
            this.loading.uncertainty = true;
            try {
                this.uncertaintyResult = await fetchUncertainty(inventoryIdx);
                this.lastUncertaintyIdx = inventoryIdx;
                this.showUncertaintyModal = true;
            } catch (error) {
                this.notifyError("불확도 분석에 실패했습니다.", error);
            } finally {
                this.loading.uncertainty = false;
            }
        },

        handleUncertaintyClose() {
            this.showUncertaintyModal = false;
        },
        async loadQualityForDetail(inventoryIdx) {
            if (!inventoryIdx || this.loading.quality) return;
            this.loading.quality = true;
            try {
                this.qualityResult = await fetchQualityReport(inventoryIdx);
                this.lastQualityIdx = inventoryIdx;
            } catch (error) {
                this.notifyError("품질 점검에 실패했습니다.", error);
            } finally {
                this.loading.quality = false;
            }
        },
        async loadUncertaintyForDetail(inventoryIdx) {
            if (!inventoryIdx || this.loading.uncertainty) return;
            this.loading.uncertainty = true;
            try {
                this.uncertaintyResult = await fetchUncertainty(inventoryIdx);
                this.lastUncertaintyIdx = inventoryIdx;
            } catch (error) {
                this.notifyError("불확도 분석에 실패했습니다.", error);
            } finally {
                this.loading.uncertainty = false;
            }
        },
        formatEmission(value) {
            if (value == null) return "-";
            return Number(value).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatPeriod(period) {
            if (!period || String(period).length !== 6) return period;
            return `${String(period).slice(0, 4)}-${String(period).slice(4, 6)}`;
        },
        notifyError(title, error) {
            const message = error?.response?.data?.message || error?.message || "알 수 없는 오류가 발생했습니다.";
            this.$bvToast.toast(message, {
                title,
                variant: "danger",
                autoHideDelay: 4000,
            });
            console.error(title, error);
        },
    },
};
</script>

<style scoped>
.inventory-manager {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    padding: 0 16px;
    background: #fafafa;
}

/* Empty 상태 */
.empty-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.inventory-empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 400px;
}

.inventory-empty-content i.fa-inbox {
    font-size: 64px;
    color: #cccccc;
    margin-bottom: 16px;
}

.inventory-empty-content h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 8px 0;
}

.inventory-empty-content p {
    font-size: 14px;
    color: #666666;
    margin: 0;
}

/* 인벤토리 그리드 */
.manager-body {
    flex: 1;
    display: flex;
    min-height: 0;
}

.inventory-grid {
    display: grid;
    grid-template-columns: 35% 65%;
    grid-template-rows: 1fr;
    gap: 16px;
    width: 100%;
    height: 100%;
    min-height: 0;
}

.inventory-list-section,
.inventory-detail-section {
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* 우측 상세: 자식 루트가 step 영역 높이를 채우도록 (scoped 경계 넘김) */
.inventory-detail-section ::v-deep .inventory-detail {
    flex: 1 1 0;
    min-height: 0;
}

@media (max-width: 1400px) {
    .inventory-grid {
        grid-template-columns: 33% 66%;
    }
}

@media (max-width: 1200px) {
    .inventory-grid {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
    }

    .inventory-list-section {
        max-height: 400px;
    }
}
</style>
